import type { KeyboardEvent } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { VisuallyHidden } from 'react-aria';
import { Transition } from 'react-transition-group';

import { useOnClickOutside } from '@/components/utils';
import Check from '@/icons/small/Check-Naked.svg';
import type { PropertyTypes } from '@/utils';

import {
  StyledArrow,
  StyledListItemContent,
  StyledOptionIcon,
  StyledSelectList,
  StyledSelectListItem,
  StyledSelectValue,
  StyledShim,
} from './SelectWithIcon.styled';

export type SelectOption = {
  value: PropertyTypes;
  label: string;
  icon?: React.ReactNode;
};

interface ISelectWithIconProps {
  options: SelectOption[];
  onChange: (value: PropertyTypes) => void;
  buttonLabel: string;
}

export const SelectWithIcon = ({
  buttonLabel,
  options,
  onChange,
}: ISelectWithIconProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(options[0].value);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const shimRef = useRef<HTMLDivElement>(null);

  const closeList = () => {
    setIsOpen(false);
  };

  const selectValue = (value: PropertyTypes) => {
    setSelectedValue(value);
    onChange(value);
    setIsOpen(false);
  };

  useOnClickOutside(listRef, closeList);

  const handleArrowUp = () => {
    setFocusedIndex(Math.max(0, focusedIndex - 1));
  };

  const handleArrowDown = () => {
    setFocusedIndex(Math.min(options.length - 1, focusedIndex + 1));
  };

  const handleEnter = () => {
    selectValue(options[focusedIndex].value);
  };

  const handleEscape = () => {
    closeList();
  };

  const keyActions: Record<string, () => void> = {
    ArrowUp: handleArrowUp,
    ArrowDown: handleArrowDown,
    Enter: handleEnter,
    Escape: handleEscape,
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') e.preventDefault();
    const action = keyActions[e.key];
    action?.();
  };

  // mouse navigation for the suggestion list
  const handleMouseEnter = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setFocusedIndex(0);
  }, []);

  const handleOptionClick = (value: PropertyTypes) => {
    selectValue(value);
  };

  const onSelectValue = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <StyledSelectValue
        onClick={onSelectValue}
        $isOpen={isOpen}
        onKeyDown={handleKeyDown}
        type="button"
      >
        <StyledOptionIcon>
          {options.find((o) => o.value === selectedValue)?.icon}
        </StyledOptionIcon>
        <StyledArrow $isOpen={isOpen} />
        <VisuallyHidden>{buttonLabel}</VisuallyHidden>
      </StyledSelectValue>
      {isOpen && (
        <>
          <Transition
            nodeRef={shimRef}
            in={isOpen}
            timeout={{
              enter: 0,
              exit: 250,
            }}
            unmountOnExit
            appear
          >
            {(shimState) => (
              <StyledShim ref={shimRef} $state={shimState}></StyledShim>
            )}
          </Transition>
          <Transition
            nodeRef={listRef}
            in={isOpen}
            timeout={{
              enter: 0,
              exit: 250,
            }}
            unmountOnExit
            appear
          >
            {(listState) => (
              <StyledSelectList $state={listState} ref={listRef}>
                {options.map((option, index) => (
                  <StyledSelectListItem
                    key={option.value}
                    $isActive={index === focusedIndex}
                    $isSelected={option.value === selectedValue}
                    onClick={() => handleOptionClick(option.value)}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <StyledListItemContent>
                      <StyledOptionIcon>{option.icon}</StyledOptionIcon>
                      {option.label}
                    </StyledListItemContent>
                    {option.value === selectedValue && <Check />}
                  </StyledSelectListItem>
                ))}
              </StyledSelectList>
            )}
          </Transition>
        </>
      )}
    </>
  );
};
